import isNil from 'lodash/isNil';
import * as actionTypes from '../../constants/actionTypes';

import { GET, POST } from '../../constants/requestMethods';
import { ACLQueryModeTypes } from '../../constants/visibilityFilters';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { getCurrentOrg, getCurrentUser } from '../../reducers/selectors';
import { pipelineSchema, recordLineageSchema, userSchema } from '../../schemas';

import {
  CRON_HISTORY_PAGINATED,
  FILES_PAGINATED,
  QUARANTINE_FILES_PAGINATED,
  QUARANTINE_SAMPLES_PAGINATED,
} from '../../constants/paginationEntitiesTypes';

import {
  METRICS_RESOURCE_NAME_SINK,
  METRICS_RESOURCE_NAME_SOURCE,
} from '../../constants/resourceTypes';

import { isStateStale } from '../../lib/StateUtils';

import { addPaginatedResource } from '../pagination';
import { omit } from 'lodash';
import moment from 'moment/moment';

export const fetchResourceMetricsAggregate = (id, resourceType) => (dispatch, getState) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_RESOURCE_METRICS_AGGREGATE_REQUEST,
        actionTypes.GET_RESOURCE_METRICS_AGGREGATE_SUCCESS,
        actionTypes.GET_RESOURCE_METRICS_AGGREGATE_FAILURE,
      ],
      endpoint: `/${resourceType}/${id}/metrics`,
      schema: null,
      requestMethod: GET,
      aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
      requestInfo: {
        resourceType,
        id,
      },
    },
  });
};

export const fetchResourceMetricsPerDay =
  (id, resourceType, dateRange = { start: null, end: null }) =>
  (dispatch, getState) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_RESOURCE_METRICS_BY_DAYS_REQUEST,
          actionTypes.GET_RESOURCE_METRICS_BY_DAYS_SUCCESS,
          actionTypes.GET_RESOURCE_METRICS_BY_DAYS_FAILURE,
        ],
        endpoint: `/${resourceType}/${id}/metrics`,
        schema: null,
        requestMethod: GET,
        aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
        payload: {
          from: dateRange.start,
          to: dateRange.end,
          aggregate: 1,
        },
        requestInfo: {
          resourceType,
          id,
        },
      },
    });
  };

export const fetchResourceFiles =
  (id, resourceType, filters = {}, defaultSortKey = null, aggregate = false) =>
  (dispatch, getState) => {
    const requestInfo = {
      paginatedResource: FILES_PAGINATED,
      resourceType,
      id,
      ...filters,
      defaultSortKey,
      aggregate,
    };
    dispatch(addPaginatedResource(requestInfo, actionTypes.GET_RESOURCE_FILES_SUCCESS));
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_RESOURCE_FILES_REQUEST,
          actionTypes.GET_RESOURCE_FILES_SUCCESS,
          actionTypes.GET_RESOURCE_FILES_FAILURE,
        ],
        endpoint: `/${resourceType}/${id}/metrics/files`,
        schema: null,
        requestMethod: GET,
        aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
        payload: {
          ...filters,
        },
        requestInfo,
      },
    });
  };

export const fetchAccountSummaryByOrg =
  (by = 'user', force = false) =>
  (dispatch, getState) => {
    const state = getState();
    const user = getCurrentUser(state);
    const org = getCurrentOrg(state);

    const count = state.metrics?.resourceCount;
    const lastUpdated = count?.lastUpdated;

    // We will only make the request after 1 minute from the last request and is not a forced request
    if ((lastUpdated?.[by] || 0) + 30 * 1000 > new Date().getTime() && !force) {
      return;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_RESOURCE_COUNT_REQUEST,
          actionTypes.GET_RESOURCE_COUNT_SUCCESS,
          actionTypes.GET_RESOURCE_COUNT_FAILURE,
        ],
        endpoint:
          by === 'user'
            ? `/users/${user.id}/account_summary?org_id=${org.id}`
            : `/orgs/${org.id}/account_summary`,
        schema: null,
        requestMethod: GET,
        requestInfo: {
          by,
        },
      },
    });
  };

export const fetchResourceCronHistory =
  (id, resourceType, filters = {}, defaultSortKey = null, aggregate = false) =>
  (dispatch, getState) => {
    const requestInfo = {
      paginatedResource: CRON_HISTORY_PAGINATED,
      aggregate,
      resourceType,
      id,
      ...filters,
      defaultSortKey,
    };
    dispatch(addPaginatedResource(requestInfo, actionTypes.GET_RESOURCE_CRON_HISTORY_SUCCESS));
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_RESOURCE_CRON_HISTORY_REQUEST,
          actionTypes.GET_RESOURCE_CRON_HISTORY_SUCCESS,
          actionTypes.GET_RESOURCE_CRON_HISTORY_FAILURE,
        ],
        endpoint: `/${resourceType}/${id}/metrics/run_summary`,
        schema: null,
        requestMethod: GET,
        aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
        payload: {
          ...filters,
        },
        requestInfo,
      },
    });
  };

export const fetchResourceFileStats = (id, resourceType) => (dispatch, getState) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_RESOURCE_FILE_STATS_REQUEST,
        actionTypes.GET_RESOURCE_FILE_STATS_SUCCESS,
        actionTypes.GET_RESOURCE_FILE_STATS_FAILURE,
      ],
      endpoint: `/${resourceType}/${id}/metrics/files_stats`,
      schema: null,
      requestMethod: GET,
      aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
      requestInfo: {
        resourceType,
        id,
      },
    },
  });
};

export const fetchAccountSummary = (userId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_USER_ACCOUNT_SUMMARY_REQUEST,
        actionTypes.GET_USER_ACCOUNT_SUMMARY_SUCCESS,
        actionTypes.GET_USER_ACCOUNT_SUMMARY_FAILURE,
      ],
      endpoint: `/users/${userId}`,
      schema: userSchema,
      requestMethod: GET,
      //Don't need acl query mode. user context only
      payload: {
        expand: 1,
      },
      requestInfo: {
        userId,
      },
    },
  });
};

export const fetchUserResourceMetricsAggregate =
  (userId, orgId, resourceType) => (dispatch, getState) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_USER_RESOURCE_METRICS_AGGREGATE_REQUEST,
          actionTypes.GET_USER_RESOURCE_METRICS_AGGREGATE_SUCCESS,
          actionTypes.GET_USER_RESOURCE_METRICS_AGGREGATE_FAILURE,
        ],
        endpoint: `/users/${userId}/metrics`,
        schema: null,
        requestMethod: GET,
        payload: {
          org_id: orgId,
          resource_type: resourceType,
        },
        requestInfo: {
          resourceType,
          userId,
        },
      },
    });
  };

export const fetchUserResourceMetricsPerDay =
  (userId, orgId, resourceType, dateRange = { start: null, end: null }) =>
  (dispatch, getState) => {
    let types = [
      actionTypes.GET_USER_RESOURCE_METRICS_N_DAYS_REQUEST,
      actionTypes.GET_USER_RESOURCE_METRICS_N_DAYS_SUCCESS,
      actionTypes.GET_USER_RESOURCE_METRICS_N_DAYS_FAILURE,
    ];

    switch (resourceType) {
      case METRICS_RESOURCE_NAME_SINK:
        types = [
          actionTypes.GET_USER_SINK_METRICS_N_DAYS_REQUEST,
          actionTypes.GET_USER_SINK_METRICS_N_DAYS_SUCCESS,
          actionTypes.GET_USER_SINK_METRICS_N_DAYS_FAILURE,
        ];
        break;
      case METRICS_RESOURCE_NAME_SOURCE:
        types = [
          actionTypes.GET_USER_SOURCE_METRICS_N_DAYS_REQUEST,
          actionTypes.GET_USER_SOURCE_METRICS_N_DAYS_SUCCESS,
          actionTypes.GET_USER_SOURCE_METRICS_N_DAYS_FAILURE,
        ];
        break;
      default:
        break;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types,
        endpoint: `/users/${userId}/metrics`,
        schema: null,
        requestMethod: GET,
        payload: {
          org_id: orgId,
          from: dateRange.start,
          to: dateRange.end,
          resource_type: resourceType,
          aggregate: 1,
        },
        requestInfo: {
          resourceType,
          userId,
        },
      },
    });
  };

export const fetchOrgResourceMetricsPerDay =
  (orgId, resourceType, dateRange = { start: null, end: null }) =>
  (dispatch, getState) => {
    let types = [
      actionTypes.GET_ORG_RESOURCE_METRICS_N_DAYS_REQUEST,
      actionTypes.GET_ORG_RESOURCE_METRICS_N_DAYS_SUCCESS,
      actionTypes.GET_ORG_RESOURCE_METRICS_N_DAYS_FAILURE,
    ];

    switch (resourceType) {
      case METRICS_RESOURCE_NAME_SINK:
        types = [
          actionTypes.GET_ORG_SINK_METRICS_N_DAYS_REQUEST,
          actionTypes.GET_ORG_SINK_METRICS_N_DAYS_SUCCESS,
          actionTypes.GET_ORG_SINK_METRICS_N_DAYS_FAILURE,
        ];
        break;
      case METRICS_RESOURCE_NAME_SOURCE:
        types = [
          actionTypes.GET_ORG_SOURCE_METRICS_N_DAYS_REQUEST,
          actionTypes.GET_ORG_SOURCE_METRICS_N_DAYS_SUCCESS,
          actionTypes.GET_ORG_SOURCE_METRICS_N_DAYS_FAILURE,
        ];
        break;
      default:
        break;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types,
        endpoint: `/orgs/${orgId}/metrics`,
        schema: null,
        requestMethod: GET,
        payload: {
          from: dateRange.start,
          to: dateRange.end,
          resource_type: resourceType,
          aggregate: 1,
        },
        requestInfo: {
          resourceType,
          orgId,
        },
      },
    });
  };

export const fetchAccountMetricsAggregate = (userId, orgId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_USER_ACCOUNT_RESOURCE_METRICS_AGG_REQUEST,
        actionTypes.GET_USER_ACCOUNT_RESOURCE_METRICS_AGG_SUCCESS,
        actionTypes.GET_USER_ACCOUNT_RESOURCE_METRICS_AGG_FAILURE,
      ],
      endpoint: `/users/${userId}/metrics/all`,
      schema: null,
      requestMethod: GET,
      payload: {
        org_id: orgId,
      },
      requestInfo: {
        userId,
        orgId,
      },
    },
  });
};

export const fetchResourceQuarantineFiles =
  (id, resourceType, filters = {}, defaultSortKey = null) =>
  (dispatch, getState) => {
    const requestInfo = {
      paginatedResource: QUARANTINE_FILES_PAGINATED,
      resourceType,
      id,
      ...filters,
      defaultSortKey,
    };
    dispatch(addPaginatedResource(requestInfo, actionTypes.GET_RESOURCE_QUARANTINE_FILES_SUCCESS));
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_RESOURCE_QUARANTINE_FILES_REQUEST,
          actionTypes.GET_RESOURCE_QUARANTINE_FILES_SUCCESS,
          actionTypes.GET_RESOURCE_QUARANTINE_FILES_FAILURE,
        ],
        endpoint: `/${resourceType}/${id}/metrics/quarantine_files`,
        schema: null,
        requestMethod: GET,
        aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
        payload: {
          ...omit(filters, 'aggregate'),
        },
        requestInfo,
      },
    });
  };

export const fetchResourceQuarantineSamples =
  (id, resourceType, filters = {}, defaultSortKey = null) =>
  (dispatch, getState) => {
    const requestInfo = {
      paginatedResource: QUARANTINE_SAMPLES_PAGINATED,
      resourceType,
      id,
      ...filters,
      defaultSortKey,
    };
    dispatch(
      addPaginatedResource(requestInfo, actionTypes.GET_RESOURCE_QUARANTINE_SAMPLES_SUCCESS),
    );
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_RESOURCE_QUARANTINE_SAMPLES_REQUEST,
          actionTypes.GET_RESOURCE_QUARANTINE_SAMPLES_SUCCESS,
          actionTypes.GET_RESOURCE_QUARANTINE_SAMPLES_FAILURE,
        ],
        endpoint: `/${resourceType}/${id}/probe/quarantine/sample`,
        schema: null,
        payload: {
          ...filters,
        },
        requestMethod: POST,
        requestInfo,
      },
    });
  };

export const fetchDashboardMetrics =
  (forceReload = false, aclQueryMode = ACLQueryModeTypes.MODE_OWNER.key) =>
  (dispatch, getState) => {
    if (!forceReload && !isStateStale(getState().metrics.dashboard.lastUpdated)) {
      return;
    }

    const user = getCurrentUser(getState());

    if (!user) {
      return;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_DASHBOARD_METRICS_REQUEST,
          actionTypes.GET_DASHBOARD_METRICS_SUCCESS,
          actionTypes.GET_DASHBOARD_METRICS_FAILURE,
        ],
        endpoint: `/users/${user.id}/flows/status_metrics`,
        schema: pipelineSchema,
        requestMethod: GET,
        aclQueryMode,
      },
    });
  };

export const fetchRecordLineage =
  (user = null, trackerId = null) =>
  (dispatch, getState) => {
    if (!trackerId) {
      return {
        type: actionTypes.GET_RECORD_LINEAGE_FAILURE,
        message: 'Please set a valid tracker id',
        error: 'Please set a valid tracker id',
        code: 400,
        receivedAt: Date.now(),
      };
    }

    if (!user) {
      return {
        type: actionTypes.GET_RECORD_LINEAGE_FAILURE,
        message: 'Could not fetch lineage for this tracker',
        error: 'Could not fetch lineage for this tracker',
        code: 400,
        receivedAt: Date.now(),
        requestInfo: {
          trackerId,
        },
      };
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_RECORD_LINEAGE_REQUEST,
          actionTypes.GET_RECORD_LINEAGE_SUCCESS,
          actionTypes.GET_RECORD_LINEAGE_FAILURE,
        ],
        endpoint: `/users/${user.id}/metrics/lineage`,
        schema: recordLineageSchema,
        requestMethod: GET,
        payload: {
          trackerId,
        },
        requestInfo: {
          userId: user.id,
          trackerId,
        },
      },
    });
  };

export const fetchUserBillableEvents =
  (data = null, optional1 = null, optional2 = null) =>
  (dispatch) => {
    let userId, startDate, endDate;
    if (typeof data === 'object') {
      ({ userId, startDate, endDate } = data);
      startDate =
        !!startDate && typeof startDate !== 'string'
          ? moment(startDate).format('YYYY-MM-DD')
          : startDate;
      endDate =
        !!endDate && typeof endDate !== 'string' ? moment(endDate).format('YYYY-MM-DD') : endDate;
    } else {
      userId = data;
      startDate = optional1;
      endDate = optional2;
    }
    if (isNil(userId)) {
      return {
        type: actionTypes.GET_USER_BILLABLE_EVENTS_TOTAL_FAILURE,
        message: 'Failed to fetch billable events',
        error: 'Failed to fetch billable events',
        code: 400,
        receivedAt: Date.now(),
      };
    }

    let payload = {};

    if (data.orgId) {
      payload.org_id = data.orgId;
    }

    if (!!startDate) {
      payload.from = startDate;
    }

    if (!!endDate) {
      payload.to = endDate;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_USER_BILLABLE_EVENTS_TOTAL_REQUEST,
          actionTypes.GET_USER_BILLABLE_EVENTS_TOTAL_SUCCESS,
          actionTypes.GET_USER_BILLABLE_EVENTS_TOTAL_FAILURE,
        ],
        endpoint: `/users/${userId}/flows/account_metrics`,
        schema: null,
        requestMethod: GET,
        payload,
        requestInfo: {
          userId,
        },
      },
    });
  };

export const fetchOrgBillableEvents =
  (data = null, optional1 = null, optional2 = null) =>
  (dispatch) => {
    let orgId, startDate, endDate;
    if (typeof data === 'object') {
      ({ orgId, startDate, endDate } = data);
      startDate =
        !!startDate && typeof startDate !== 'string'
          ? moment(startDate).format('YYYY-MM-DD')
          : startDate;
      endDate =
        !!endDate && typeof endDate !== 'string' ? moment(endDate).format('YYYY-MM-DD') : endDate;
    } else {
      orgId = data;
      startDate = optional1;
      endDate = optional2;
    }
    if (isNil(orgId)) {
      return {
        type: actionTypes.GET_ORG_BILLABLE_EVENTS_TOTAL_FAILURE,
        message: 'Failed to fetch billable events',
        error: 'Failed to fetch billable events',
        code: 400,
        receivedAt: Date.now(),
      };
    }

    let payload = {};

    if (!!startDate) {
      payload.from = startDate;
    }

    if (!!endDate) {
      payload.to = endDate;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_ORG_BILLABLE_EVENTS_TOTAL_REQUEST,
          actionTypes.GET_ORG_BILLABLE_EVENTS_TOTAL_SUCCESS,
          actionTypes.GET_ORG_BILLABLE_EVENTS_TOTAL_FAILURE,
        ],
        endpoint: `/orgs/${orgId}/flows/account_metrics`,
        schema: null,
        requestMethod: GET,
        payload,
        requestInfo: {
          orgId,
        },
      },
    });
  };

/**
 * Set the user's preferred flow metrics interval.
 */
export const setFlowMetricsInterval = (interval) => ({
  type: actionTypes.SET_FLOW_METRICS_INTERVAL,
  interval,
});

export const loadFlowMetrics =
  (flowNodeId, options = {}) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_FLOW_METRICS_REQUEST,
          actionTypes.GET_FLOW_METRICS_SUCCESS,
          actionTypes.GET_FLOW_METRICS_FAILURE,
        ],
        endpoint: `/flows/${flowNodeId}/metrics`,
        schema: null,
        payload: options,
        requestMethod: GET,
        requestInfo: { flowNodeId },
      },
    });
  };

/**
 * Load metrics for a specific flow run.
 * GET /flows/<flowNodeId>/metrics?run_id=<runId>
 */
export const loadFlowMetricsByRunId = (flowNodeId, runId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_FLOW_METRICS_REQUEST,
        actionTypes.GET_FLOW_METRICS_SUCCESS,
        actionTypes.GET_FLOW_METRICS_FAILURE,
      ],
      endpoint: `/flows/${flowNodeId}/metrics`,
      schema: null,
      payload: { run_id: runId },
      requestMethod: GET,
      requestInfo: { flowNodeId, runId },
    },
  });
};

export const fetchFlowRunSummary =
  (flowNodeId, options = {}) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_FLOW_RUN_SUMMARY_REQUEST,
          actionTypes.GET_FLOW_RUN_SUMMARY_SUCCESS,
          actionTypes.GET_FLOW_RUN_SUMMARY_FAILURE,
        ],
        endpoint: `/flows/${flowNodeId}/metrics`,
        schema: null,
        payload: {
          ...options,
          groupby: 'runId',
        },
        requestMethod: GET,
        requestInfo: {
          flowNodeId,
          page: options.page,
        },
      },
    });
  };

export const clearFlowRunSummary = (originNodeId) => {
  return {
    type: actionTypes.CLEAR_FLOW_RUN_SUMMARY,
    payload: { originNodeId },
  };
};

export const fetchFlowRunLogs =
  (flowNodeId, options = {}) =>
  (dispatch) => {
    const payload = {
      ...options,
      run_id: options.runId,
    };
    delete payload.runId;

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_FLOW_RUN_LOGS_REQUEST,
          actionTypes.GET_FLOW_RUN_LOGS_SUCCESS,
          actionTypes.GET_FLOW_RUN_LOGS_FAILURE,
        ],
        endpoint: `/flows/${flowNodeId}/logs`,
        schema: null,
        payload,
        requestMethod: GET,
        requestInfo: {
          flowNodeId,
          runId: options.runId,
          page: options.page,
        },
      },
    });
  };

export const fetchFlowRunLogsMetadata =
  (flowNodeId, options = {}) =>
  (dispatch) => {
    const payload = {
      ...options,
      run_id: options.runId || 0,
    };
    delete payload.runId;

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_FLOW_RUN_LOGS_METADATA_REQUEST,
          actionTypes.GET_FLOW_RUN_LOGS_METADATA_SUCCESS,
          actionTypes.GET_FLOW_RUN_LOGS_METADATA_FAILURE,
        ],
        endpoint: `/flows/${flowNodeId}/logs`,
        schema: null,
        payload,
        requestMethod: GET,
      },
    });
  };

export const fetchFlowRunLogsWithoutRunId =
  (flowNodeId, options = {}) =>
  (dispatch) => {
    const payload = { ...options, run_id: 0 };

    delete payload.runId;

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_FLOW_RUN_LOGS_REQUEST,
          actionTypes.GET_FLOW_RUN_LOGS_SUCCESS,
          actionTypes.GET_FLOW_RUN_LOGS_FAILURE,
        ],
        endpoint: `/flows/${flowNodeId}/logs`,
        schema: null,
        payload,
        requestMethod: GET,
        requestInfo: {
          flowNodeId,
          page: options.page,
        },
      },
    });
  };

export const clearFlowRunLogs = () => {
  return {
    type: actionTypes.CLEAR_FLOW_RUN_LOGS,
  };
};

export const clearFlowRunLogsWithFacetsV2 = () => {
  return {
    type: actionTypes.CLEAR_FLOW_RUN_LOGS_WITH_FACETS_V2,
  };
};
